import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InfoIcon } from "../Auth/onboarding/icons";
import { ChatBubbleIcon, UsersIcon, BookIcon } from "../Dashboard/icons";
import { ArrowRight } from "lucide-react";
import { CrownIcon } from "./icons";
import { formatLimit, planLabel, cancelSubscription } from "../../actions/billing";
import { statusBadgeClass } from "./statusBadge";
import ConfirmDialog from "../../Components/ConfirmDialog";

const PLAN_BLURBS = {
  free: "Try Hostor with a single assistant and a small monthly allowance.",
  pro: "For growing teams that need more assistants and a bigger chat allowance.",
  agency: "Run assistants for several clients, with room to scale.",
  business: "Unlimited assistants and conversations for larger operations.",
};

// The subscription record answers with the plan and its headline caps. The
// per-message limits live on the plan tier instead, so the matching row from
// GET /billing/plans fills those in - reading them off the subscription is why
// both showed a dash.
const limitsFrom = (subscription, plans) => {
  const plan = subscription?.plan;
  const tier =
    plans.find((entry) => entry.id === plan || entry.plan === plan || entry.key === plan) ||
    {};

  const pick = (...values) =>
    values.find((value) => typeof value === "number");

  return {
    maxAssistants: pick(subscription?.maxAssistants, tier.maxAssistants),
    monthlyChatLimitPerAssistant: pick(
      subscription?.monthlyChatLimitPerAssistant,
      tier.monthlyChatLimitPerAssistant,
    ),
    maxTrainingMessages: pick(
      subscription?.maxTrainingMessages,
      tier.maxTrainingMessages,
    ),
    maxVisitorMessages: pick(
      subscription?.maxVisitorMessages,
      tier.maxVisitorMessages,
    ),
  };
};

export default function CurrentPlanCard({
  subscription,
  plans = [],
  onChangePlan,
  onCancelled,
}) {
  const dispatch = useDispatch();
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState("");

  // Stands in until GET /billing/subscription answers, so the card is never
  // blank on a cold load.
  const fallback = useSelector((state) => state.auth.user?.subscription);
  const assistantCount = useSelector((state) => state.assistants.list.length);

  const record = subscription || fallback;
  const plan = record?.plan || "free";
  const name = planLabel(plan);
  const status = record?.status;
  const limits = limitsFrom(record, plans);

  // Free has no subscription behind it, so there is nothing to turn off
  // there - the endpoint answers 400 for exactly that case.
  const canCancel = plan !== "free" && Boolean(record);
  // Already cancelled: auto-renewal is off and the paid period is running
  // out, or has. Cancelling again answers 400, so the button stays visible
  // but goes flat - the plan is still the current one until it ends, and a
  // button that vanishes reads as though something else changed.
  const alreadyCancelled =
    status === "cancelled" || status === "ended" || Boolean(record?.cancelledAt);
  // The backend words this better than we can, and it is the authority on
  // what cancelling actually does.
  const cancelNote =
    record?.paymentMethod?.cancelNote ||
    "This stops all future payments. Your plan keeps working until the end of the period you have already paid for; after that your account is paused until you choose a plan again.";

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError("");

    const result = await dispatch(cancelSubscription());

    setCancelling(false);
    setConfirming(false);

    if (!result?.success) {
      setCancelError(result?.message || "We couldn't turn off auto-renewal.");
      return;
    }

    onCancelled?.();
  };
  const stats = [
    {
      icon: <UsersIcon />,
      variant: "indigo",
      limit: limits.maxAssistants,
      value: formatLimit(limits.maxAssistants),
      label: "Assistants",
      tooltip: `Assistants you can run on this plan. You have ${assistantCount}.`,
    },
    {
      icon: <ChatBubbleIcon />,
      variant: "green",
      limit: limits.monthlyChatLimitPerAssistant,
      value: formatLimit(limits.monthlyChatLimitPerAssistant),
      label: "Chats / assistant / month",
      tooltip:
        "Conversations each assistant can handle in a billing month.",
    },
    {
      icon: <BookIcon />,
      variant: "amber",
      limit: limits.maxTrainingMessages,
      value: formatLimit(limits.maxTrainingMessages),
      label: "Training messages",
      tooltip: "Messages used to train each assistant on your content.",
    },
    {
      icon: <ChatBubbleIcon />,
      variant: "blue",
      limit: limits.maxVisitorMessages,
      value: formatLimit(limits.maxVisitorMessages),
      label: "Visitor messages",
      tooltip: "Messages a single visitor can send in one conversation.",
    },
  ];

  return (
    <section className="dash-card bill-plan-card">
      <div className="bill-plan-header">
        <h2 className="cz-card-title">Current plan</h2>
      </div>

      <div className="bill-plan-row">
        <span className="bill-plan-icon">
          <CrownIcon />
        </span>
        <div className="bill-plan-info">
          <div className="bill-plan-name">
            <strong>{name} plan</strong>
            {status && (
              <span className={statusBadgeClass(status)}>
                {planLabel(status)}
              </span>
            )}
          </div>
          <p className="bill-plan-desc">
            {PLAN_BLURBS[plan] ||
              "Your current Hostor subscription and its monthly allowances."}
          </p>
        </div>
        <div className="bill-plan-actions">
        <button
          type="button"
          className="onboarding__btn onboarding__btn--primary bill-change-btn"
          onClick={onChangePlan}
        >
          Change plan
          <ArrowRight size={16} strokeWidth={2.4} />
        </button>

        {canCancel && (
          <button
            type="button"
            className="onboarding__btn onboarding__btn--secondary bill-cancel-btn"
            onClick={() => setConfirming(true)}
            disabled={cancelling || alreadyCancelled}
            title={
              alreadyCancelled
                ? record?.access?.message ||
                  "This plan is already cancelled and will not renew."
                : undefined
            }
          >
            {alreadyCancelled ? "Cancelled" : "Cancel plan"}
          </button>
        )}
        </div>
      </div>

      <div className="bill-plan-stats">
        {/* A limit the API does not report is left out rather than shown as a
            dash, which reads as "zero" or "broken" instead of "not stated". */}
        {stats
          .filter((stat) => typeof stat.limit === "number")
          .map((stat) => (
            <div className="bill-plan-stat" key={stat.label}>
              <span className={`dash-stat-icon dash-stat-icon--${stat.variant}`}>{stat.icon}</span>
              <div className="bill-plan-stat-text">
                <strong>{stat.value}</strong>
                <span>
                  {stat.label}
                  <span className="bill-info-icon" title={stat.tooltip}>
                    <InfoIcon />
                  </span>
                </span>
              </div>
            </div>
          ))}
      </div>
      {cancelError && <p className="auth-error">{cancelError}</p>}

      <ConfirmDialog
        open={confirming}
        title={`Cancel your ${name} plan?`}
        message={cancelNote}
        confirmLabel={cancelling ? "Cancelling…" : "Yes, cancel plan"}
        cancelLabel="Keep my plan"
        onConfirm={handleCancel}
        onCancel={() => !cancelling && setConfirming(false)}
      />
    </section>
  );
}
